import { useMutation, useQueryClient } from 'react-query';
import { IntegrationProps } from 'services/hooks/integration-service';

type Context = { previousIntegrations: IntegrationProps[] };
function isContext(value: unknown): value is Context {
  return (
    typeof value === 'object' && value !== null && value.hasOwnProperty('previousIntegrations')
  );
}

const useDeleteIntegration = ({
  deleteQuery,
  queryName,
  onSuccess,
  removeOnSuccess = false
}: {
  deleteQuery: any;
  queryName: string;
  onSuccess?: () => void;
  removeOnSuccess?: boolean;
}) => {
  const queryClient = useQueryClient();

  const deleteIntegrationMutation = useMutation(
    async (uuid?: string) => {
      deleteQuery(uuid);
    },
    {
      onMutate: async (uuid) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries([queryName]);

        // Snapshot the previous value
        const previousIntegrations = queryClient.getQueryData<IntegrationProps[]>([queryName]);

        // Optimistically update to the new value
        if (previousIntegrations) {
          const data = previousIntegrations.filter((integration) => {
            return integration.uuid !== uuid;
          });
          queryClient.setQueryData<IntegrationProps[]>([queryName], {
            ...previousIntegrations,
            ...data
          });
        }

        return { previousIntegrations };
      },
      onSuccess: async () => {
        if (removeOnSuccess) {
          // When deleting a user form the single user page, we want to ensure we have fresh data when being redirected back to the index.
          // However, when deleting from the index we don't want to reload the whole index
          await queryClient.removeQueries(queryName);
        }
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (err, variables, context) => {
        console.log(err);
        console.log(variables);
        // If the mutation fails, use the context returned from onMutate to roll back
        if (isContext(context)) {
          queryClient.setQueryData<IntegrationProps[]>([queryName], context.previousIntegrations);
        }
      }
    }
  );

  return deleteIntegrationMutation;
};

export default useDeleteIntegration;
